'use client';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useState, useMemo, useCallback, cloneElement } from 'react';
import styled, { css } from 'styled-components';
import { debounce } from 'throttle-debounce';
import { zIndexUtil } from '../../helpers/zIndexUtil.js';
import { withTitan } from '../../hocs/withTitan/withTitan.js';
import { media } from '../../mixins/media.js';
import '../../mixins/keyboardFocus.js';
import '../../mixins/markStyle.js';
import '../../mixins/scrollbarStyle.js';
import '../../mixins/textStyle.js';
import { ActionMenu } from '../ActionMenu/ActionMenu.js';
import { Button } from '../Button/Button.js';
import { ButtonGroup } from '../ButtonGroup/ButtonGroup.js';
import { Dimmer } from '../Dimmer/Dimmer.js';
import { Flex } from '../Flex/Flex.js';
import '../Icons/AlignStartHorizontalIcon.js';
import '../Icons/ArchiveIcon.js';
import '../Icons/ArrowDownIcon.js';
import '../Icons/ArrowLeftBottomIcon.js';
import '../Icons/ArrowLeftIcon.js';
import '../Icons/ArrowLeftTopIcon.js';
import '../Icons/ArrowRightBottomIcon.js';
import '../Icons/ArrowRightIcon.js';
import '../Icons/ArrowRightTopIcon.js';
import '../Icons/BagIcon.js';
import '../Icons/BasketIcon.js';
import '../Icons/BellFilledIcon.js';
import '../Icons/BellNotificationIcon.js';
import '../Icons/BellOutlineIcon.js';
import '../Icons/BinocularsIcon.js';
import '../Icons/BlocksIcon.js';
import '../Icons/BoltIcon.js';
import '../Icons/BookIcon.js';
import '../Icons/BookmarkAddFilledIcon.js';
import '../Icons/BookmarkAddOutlineIcon.js';
import '../Icons/BookmarkIcon.js';
import '../Icons/BookOpenedIcon.js';
import '../Icons/BoxAltIcon.js';
import '../Icons/BusinessIcon.js';
import '../Icons/CalendarIcon.js';
import '../Icons/CancelIcon.js';
import '../Icons/CardsIcon.js';
import '../Icons/ChatAltIcon.js';
import '../Icons/ChatIcon.js';
import '../Icons/CheckboxIcon.js';
import '../Icons/CheckCirlcleFilledIcon.js';
import '../Icons/CheckSquareIcon.js';
import '../Icons/ChevronDoubleLeftIcon.js';
import '../Icons/ChevronDoubleRightIcon.js';
import { ChevronDownIcon } from '../Icons/ChevronDownIcon.js';
import '../Icons/ChevronLeftIcon.js';
import '../Icons/ChevronRightIcon.js';
import { ChevronUpIcon } from '../Icons/ChevronUpIcon.js';
import '../Icons/ClipboardEditIcon.js';
import '../Icons/ClipboardIcon.js';
import '../Icons/CloseIcon.js';
import '../Icons/CollapseIcon.js';
import '../Icons/ControlsIcon.js';
import '../Icons/ControlsVerticalAltIcon.js';
import '../Icons/CopyIcon.js';
import '../Icons/CutIcon.js';
import '../Icons/DangerIcon.js';
import '../Icons/DownloadCloudIcon.js';
import '../Icons/DownloadIcon.js';
import '../Icons/DownloadPdfIcon.js';
import '../Icons/DrawerOpenIcon.js';
import '../Icons/EllipsisIcon.js';
import '../Icons/EllypsisVerticalIcon.js';
import '../Icons/EnvelopeIcon.js';
import '../Icons/ErrorIcon.js';
import '../Icons/ExitIcon.js';
import '../Icons/ExpandIcon.js';
import '../Icons/ExternalLinkIcon.js';
import '../Icons/EyeCrossedIcon.js';
import '../Icons/EyeIcon.js';
import '../Icons/FacebookIcon.js';
import '../Icons/FavouriteIcon.js';
import '../Icons/FileBadgeIcon.js';
import '../Icons/FileIcon.js';
import '../Icons/FileSearchIcon.js';
import '../Icons/FileType2Icon.js';
import '../Icons/FilmIcon.js';
import '../Icons/FitscreenIcon.js';
import '../Icons/FocusIcon.js';
import '../Icons/FolderAddIcon.js';
import '../Icons/FolderClosedIcon.js';
import '../Icons/FolderIcon.js';
import '../Icons/FutureIcon.js';
import '../Icons/GalleryVerticalEndIcon.js';
import '../Icons/GridIcon.js';
import '../Icons/HelpIcon.js';
import '../Icons/HistoryIcon.js';
import '../Icons/HomeIcon.js';
import '../Icons/InformationIcon.js';
import '../Icons/InstagramIcon.js';
import '../Icons/LibraryIcon.js';
import '../Icons/LightbulbIcon.js';
import '../Icons/LinkedinIcon.js';
import '../Icons/ListIcon.js';
import '../Icons/ListSearchIcon.js';
import '../Icons/Loader2Icon.js';
import '../Icons/LockAltIcon.js';
import '../Icons/LockAltOpenIcon.js';
import '../Icons/LockIcon.js';
import '../Icons/LockOpenIcon.js';
import '../Icons/MagazineIcon.js';
import '../Icons/MailIcon.js';
import '../Icons/Maximize2Icon.js';
import '../Icons/MenuCollapsable2Icon.js';
import '../Icons/MenuCollapsableIcon.js';
import '../Icons/MenuDotsIcon.js';
import '../Icons/MenuIcon.js';
import '../Icons/MinusIcon.js';
import '../Icons/NewsIcon.js';
import '../Icons/NtfrIcon.js';
import '../Icons/OkCircleIcon.js';
import '../Icons/OkIcon.js';
import '../Icons/PencilIcon.js';
import '../Icons/PenSquareIcon.js';
import '../Icons/People2Icon.js';
import '../Icons/PeopleIcon.js';
import '../Icons/PersonIcon.js';
import '../Icons/PhoneCallIcon.js';
import '../Icons/PlayFilledIcon.js';
import '../Icons/PlayIcon.js';
import '../Icons/PlusCircleIcon.js';
import '../Icons/PlusIcon.js';
import '../Icons/PlusSquareIcon.js';
import '../Icons/PrintIcon.js';
import '../Icons/RadioIcon.js';
import '../Icons/RefreshCwIcon.js';
import '../Icons/RotateIcon.js';
import '../Icons/SearchIcon.js';
import '../Icons/SettingsIcon.js';
import '../Icons/ShareIcon.js';
import '../Icons/ShieldIcon.js';
import '../Icons/SocialXIcon.js';
import '../Icons/StarFilledIcon.js';
import '../Icons/StarOutlineIcon.js';
import '../Icons/StickyNoteIcon.js';
import '../Icons/SwapHorizontalIcon.js';
import '../Icons/TablePropertiesIcon.js';
import '../Icons/TasklistIcon.js';
import '../Icons/ThumbDownIcon.js';
import '../Icons/ThumbsDownIcon.js';
import '../Icons/ThumbsUpIcon.js';
import '../Icons/ThumbUpIcon.js';
import '../Icons/TimeIcon.js';
import '../Icons/TimerIcon.js';
import '../Icons/Trash2Icon.js';
import '../Icons/TrashIcon.js';
import '../Icons/UpgradeIcon.js';
import '../Icons/UploadIcon.js';
import '../Icons/UserIcon.js';
import '../Icons/VideoIcon.js';
import '../Icons/WorldIcon.js';
import '../Icons/XposiIcon.js';
import '../Icons/YoutubeIcon.js';
import { InputVariant } from '../Input/Input.js';
import { MenuItem } from '../Menu/MenuItem.js';
import { Portal } from '../Portal/Portal.js';
import { SearchForm } from '../SearchForm/SearchForm.js';
import { useTable } from '../Table/TableProvider.js';

function getSearchContainerCss(shouldRenderMobileView) {
  if (shouldRenderMobileView === undefined) {
    return media.lessThan('s')`
      flex-basis: 100%;
    `;
  }
  return shouldRenderMobileView ? 'flex-basis: 100%' : undefined;
}
const StyledSearchContainer = styled.div.withConfig({
  componentId: "sc-imxh4t-0"
})(["margin-left:auto;", " ", ""], ({
  shouldRenderMobileView
}) => css(["", ""], getSearchContainerCss(shouldRenderMobileView)), ({
  searchInputWidth
}) => searchInputWidth === 'large' && css(["", ""], media.greaterThan('s')`
        ${({
  theme
}) => `width: ${theme.components.tableCommandBar.searchInputWidth.large};`}
      `));
const StyledActionMenu = styled(ActionMenu).withConfig({
  componentId: "sc-imxh4t-1"
})(["z-index:", ";"], zIndexUtil.tableCommandBarActions);
const MobileActionsWrapper = styled.div.withConfig({
  componentId: "sc-imxh4t-2"
})(["display:block;", ""], media.greaterThan('s')`
    display: none;
  `);
const ActionsWrapper = styled.div.withConfig({
  componentId: "sc-imxh4t-3"
})(["display:none;", ""], media.greaterThan('s')`
    display: block;
  `);
const TableCommandBar = /*#__PURE__*/withTitan(({
  labelActionButton = 'Acties',
  buttons = [],
  children,
  dimmedActionsZIndex = zIndexUtil.tableCommandBarActions,
  spaceAfter = 6,
  minSearchLength = 0,
  onSearch,
  shouldClearSortOnSearch,
  shouldHideSearch,
  shouldRenderMobileView,
  searchPlaceholder = 'Zoeken...',
  searchInputWidth = 'small',
  testId = 'tableCommandBar'
}) => {
  const {
    instance
  } = useTable();
  const buttonCount = buttons.length;
  const [isActionMenuShown, setIsActionMenuShown] = useState(false);
  const [filterValue, setFilterValue] = useState(instance.getState().globalFilter || '');
  const onChange = useMemo(() => debounce(200, value => {
    if (shouldClearSortOnSearch) {
      instance.setSorting([]);
    }
    instance.setGlobalFilter(value || '');
  }), [instance, shouldClearSortOnSearch]);
  const handleChange = useCallback(query => {
    setFilterValue(query);
    if (query.length > 0 && query.length < minSearchLength) {
      return;
    }
    onChange(query);
    onSearch === null || onSearch === void 0 ? void 0 : onSearch(query);
  }, [onChange, onSearch, minSearchLength]);
  const actions = useMemo(() => buttonCount > 0 && jsx(ButtonGroup, {
    children: buttons.map(button => cloneElement(button, {
      size: 'S'
    }))
  }), [buttonCount, buttons]);
  const mobileActions = useMemo(() => buttonCount > 1 ? jsxs(Fragment, {
    children: [isActionMenuShown && jsx(Portal, {
      children: jsx(Dimmer, {
        testId: "tableCommandBarDimmer",
        zIndex: dimmedActionsZIndex - 10
      })
    }), jsx(StyledActionMenu, {
      isShown: isActionMenuShown,
      label: labelActionButton,
      popoverZIndex: dimmedActionsZIndex,
      testId: "tableCommandBarActionMenu",
      trigger: jsx(Button, {
        iconRight: isActionMenuShown ? jsx(ChevronUpIcon, {}) : jsx(ChevronDownIcon, {}),
        size: "S",
        variant: "primary",
        children: labelActionButton
      }),
      onToggle: () => setIsActionMenuShown(!isActionMenuShown),
      children: buttons.map(({
        props
      }, i) => jsx(MenuItem
      // eslint-disable-next-line react/no-array-index-key
      , {
        href: props.href,
        item: {
          label: props.children
        },
        onClick: props.onClick
      }, `tbc-menu-item-${i}`))
    })]
  }) : actions, [buttonCount, isActionMenuShown, dimmedActionsZIndex, labelActionButton, buttons, actions]);
  const renderActions = useCallback(() => {
    if (shouldRenderMobileView === undefined) {
      return jsxs(Fragment, {
        children: [jsx(MobileActionsWrapper, {
          children: mobileActions
        }), jsx(ActionsWrapper, {
          children: actions
        })]
      });
    }
    return shouldRenderMobileView ? mobileActions : actions;
  }, [actions, mobileActions, shouldRenderMobileView]);
  return jsxs(Flex, {
    alignItems: "center",
    flexWrap: "wrap",
    gap: 2,
    justifyContent: "space-between",
    spaceAfter: spaceAfter,
    testId: testId,
    children: [renderActions(), children && jsx(Flex, {
      alignItems: "center",
      flex: "1 1 auto",
      gap: 2,
      justifyContent: {
        xs: 'start',
        m: 'end'
      },
      children: children
    }), !shouldHideSearch && jsx(StyledSearchContainer, {
      searchInputWidth: searchInputWidth,
      shouldRenderMobileView: shouldRenderMobileView,
      children: jsx(SearchForm, {
        autoComplete: "off",
        placeholder: searchPlaceholder,
        size: "s",
        testId: `${testId}Form`,
        value: filterValue,
        variant: InputVariant.FILLED,
        onInputChange: handleChange,
        onSubmit: handleChange
      })
    })]
  });
}, {
  name: 'TableCommandBar'
});

export { TableCommandBar };
//# sourceMappingURL=TableCommandBar.js.map
